// Onboarding pipeline states and the firm status page (docs/contract.md §6, §7).
// Labels are the Status artboard's wording.
import { REVIEW_STEP } from './answers.js';
export const ONBOARDING_STATUSES = [
    'invited', 'opened', 'in_progress', 'submitted', 'kickoff_booked',
    'plan_drafted', 'plan_approved', 'handed_off', 'expired', 'withdrawn',
];
/** Allowed transitions. Anything not listed is rejected by the API. */
export const TRANSITIONS = {
    invited: ['opened', 'expired', 'withdrawn'],
    opened: ['in_progress', 'submitted', 'expired', 'withdrawn'],
    in_progress: ['submitted', 'expired', 'withdrawn'],
    submitted: ['kickoff_booked', 'plan_drafted', 'withdrawn'],
    kickoff_booked: ['submitted', 'plan_drafted', 'withdrawn'], // back to submitted when the Discovery Session is cancelled
    plan_drafted: ['plan_approved', 'withdrawn'],
    plan_approved: ['handed_off', 'withdrawn'],
    handed_off: [],
    expired: ['invited', 'opened', 'in_progress', 'withdrawn'], // reissue restores the pre-expiry state
    withdrawn: [],
};
export const canTransition = (from, to) => TRANSITIONS[from].includes(to);
export function stageLabel(status, currentStep) {
    switch (status) {
        case 'invited': return 'Invited · not opened';
        case 'opened': return 'Opened';
        case 'in_progress': return `In progress · step ${Math.max(1, Math.min(REVIEW_STEP, currentStep))} of ${REVIEW_STEP}`;
        case 'submitted': return 'Submitted';
        case 'kickoff_booked': return 'Discovery Session booked';
        case 'plan_drafted': return 'Plan drafted';
        case 'plan_approved': return 'Plan approved';
        case 'handed_off': return 'Handed to migration project';
        case 'expired': return 'Link expired';
        case 'withdrawn': return 'Withdrawn';
    }
}
export const STAGES = [
    // `blurb` is what happens in the stage; `outcome` is what the firm has at the end of it (the status page shows both).
    { id: 'plan', title: 'Plan', blurb: 'Tell us about your firm, meet us for a Discovery Session and sign the agreement.', outcome: 'A migration plan you’ve approved.' },
    { id: 'prepare', title: 'Prepare', blurb: 'We set up your portal with your clients, work and documents, and you check it.', outcome: 'Your data is ready for launch.' },
    { id: 'launch', title: 'Launch', blurb: 'Guided training, help getting your firm set up, and your latest data on launch day.', outcome: 'Your firm is live in the portal.' },
    { id: 'succeed', title: 'Succeed', blurb: 'We’re with you as you configure the portal, and here whenever your team needs us.', outcome: 'Your team works with confidence.' },
];
export const STATUS_STEPS = [
    { id: 'plan.intake', stage: 'plan', label: 'Onboarding questionnaire', detail: null, source: 'auto' },
    { id: 'plan.kickoff_booked', stage: 'plan', label: 'Discovery Session booked', detail: null, source: 'auto' },
    // Done automatically when the in-app agreement is fully signed (statusSteps reports source 'auto' then);
    // staff can still tick it on the review's Status page tab for an agreement signed outside the app.
    { id: 'plan.agreement', stage: 'plan', label: 'Sign the agreement', detail: null, source: 'manual' },
    { id: 'plan.migration_plan', stage: 'plan', label: 'Your migration plan', detail: 'What moves, what stays, the order it moves in and your timeline. We review it together at kickoff.', source: 'auto' },
    { id: 'plan.firm_approves', stage: 'plan', label: 'You approve the plan', detail: 'Nothing in your current systems changes before this.', source: 'auto' },
    { id: 'prepare.remaining_files', stage: 'prepare', label: 'Share any remaining migration files, with step-by-step guides', detail: null, source: 'manual' },
    { id: 'prepare.test_load', stage: 'prepare', label: 'We test-load your data into a private copy of your firm', detail: null, source: 'manual' },
    { id: 'prepare.firm_reviews', stage: 'prepare', label: 'You review the results: counts, any records we need your help with', detail: null, source: 'manual' },
    { id: 'prepare.freeze_date_agreed', stage: 'prepare', label: 'We agree your freeze date and launch day', detail: null, source: 'manual' },
    { id: 'launch.walkthrough', stage: 'launch', label: 'A guided walkthrough of the portal for your team', detail: null, source: 'manual' },
    { id: 'launch.freeze_exports', stage: 'launch', label: 'Fresh exports on your freeze date, so nothing that changed is lost', detail: null, source: 'manual' },
    { id: 'launch.final_load', stage: 'launch', label: 'Final load and checks', detail: null, source: 'manual' },
    { id: 'launch.go_live', stage: 'launch', label: 'Go-live: your team switches on and you invite your clients', detail: null, source: 'manual' },
    { id: 'succeed.support', stage: 'succeed', label: 'Hands-on support through your first weeks', detail: null, source: 'manual' },
    { id: 'succeed.check_ins', stage: 'succeed', label: 'Check-ins to make sure the new way of working is sticking', detail: null, source: 'manual' },
    { id: 'succeed.sign_off', stage: 'succeed', label: 'You confirm everything is working and we close out the migration', detail: null, source: 'manual' },
];
const ORDER = ['invited', 'opened', 'in_progress', 'submitted', 'kickoff_booked', 'plan_drafted', 'plan_approved', 'handed_off'];
const reached = (status, target) => ORDER.indexOf(status) >= ORDER.indexOf(target);
export function statusSteps(i) {
    const auto = {
        'plan.intake': { done: i.submittedAt !== null, at: i.submittedAt },
        'plan.kickoff_booked': { done: i.kickoffAt !== null || reached(i.status, 'plan_drafted'), at: i.kickoffAt },
        'plan.migration_plan': { done: reached(i.status, 'plan_drafted'), at: i.planDraftedAt },
        'plan.firm_approves': { done: reached(i.status, 'plan_approved'), at: i.planApprovedAt },
    };
    let nextAssigned = false;
    return STATUS_STEPS.map((def) => {
        // An agreement signed in the app wins over the manual tick and makes the step automatic.
        if (def.id === 'plan.agreement' && i.agreementSignedAt) {
            return { ...def, source: 'auto', state: 'done', at: i.agreementSignedAt };
        }
        const s = def.source === 'auto' ? auto[def.id] : (i.manual[def.id] ?? { done: false, at: null });
        let state = s.done ? 'done' : 'todo';
        if (!s.done && !nextAssigned) {
            state = 'next';
            nextAssigned = true;
        }
        return { ...def, state, at: s.at };
    });
}
export function stageViews(steps) {
    let current = null;
    return STAGES.map((st, idx) => {
        const mine = steps.filter((s) => s.stage === st.id);
        const done = mine.filter((s) => s.state === 'done').length;
        let state;
        if (done === mine.length)
            state = 'done';
        else if (current === null) {
            state = 'in_progress';
            current = st.id;
        }
        else
            state = STAGES.findIndex((x) => x.id === current) === idx - 1 ? 'up_next' : 'later';
        return { id: st.id, title: st.title, blurb: st.blurb, outcome: st.outcome, state, done, total: mine.length };
    });
}
//# sourceMappingURL=status.js.map